import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import { getProductLabels, formatProductName } from "../product-utils";
import { toCsv, downloadCsv } from "../csv-utils";
import type { AuditEntry, FlagAuditEntry, FlagCell } from "../types";

import "@bee/pattern-library";

// Formats a flag audit from/to value for display. `to` is a literal string
// ("off") on create, a FlagCell on update, and null on delete/create's `from`.
function formatFlagAuditValue(value: FlagCell | string | null): string {
  if (value == null) return "—";
  if (typeof value === "string") return value;
  if (!value.enabled) return "off";
  return value.rolloutPercent < 100 ? `on (${value.rolloutPercent}%)` : "on";
}

@customElement("audit-page")
export class AuditPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .controls {
      display: flex;
      align-items: center;
      gap: var(--spacing-12, 12px);
      margin-bottom: var(--spacing-16, 16px);
    }

    .filter-input {
      width: 220px;
    }

    .entry-count {
      font-size: 13px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-left: auto;
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
    }

    .entry-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-12, 12px);
    }

    bee-card {
      --padding: var(--spacing-16, 16px);
    }

    bee-card::part(body) {
      padding: var(--spacing-16, 16px);
    }

    bee-card::part(base) {
      transition: box-shadow 0.15s ease;
    }

    bee-card:hover::part(base) {
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }

    .entry-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--spacing-8, 8px);
    }

    .entry-left {
      display: flex;
      align-items: center;
      gap: var(--spacing-8, 8px);
    }

    .entry-number {
      font-size: 12px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      white-space: nowrap;
    }

    .entry-date {
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      white-space: nowrap;
    }

    .promotion-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: var(--radius-sm, 6px);
      font-size: 12px;
      font-weight: 500;
      margin-top: var(--spacing-8, 8px);
    }

    .promotion-badge.to-stage {
      background: var(--colors-background-bg-warning-50, #fffcf0);
      color: var(--colors-foreground-fg-warning-700, #b54708);
    }

    .promotion-badge.to-prod {
      background: var(--colors-background-bg-success-50, #f0fdf4);
      color: var(--colors-foreground-fg-success-700, #15803d);
    }

    .change-list {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-4, 4px) var(--spacing-8, 8px);
      margin-top: var(--spacing-8, 8px);
    }

    .change-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      background: var(--colors-background-bg-gray-50, #f5f6f8);
      border-radius: var(--radius-sm, 6px);
      font-size: 12px;
      font-family: monospace;
    }

    .change-name {
      font-weight: 500;
      color: var(--colors-text-brand-900, #002266);
    }

    .change-arrow {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .change-from {
      color: var(--colors-foreground-fg-danger-500, #c62828);
      text-decoration: line-through;
    }

    .change-to {
      color: var(--colors-foreground-fg-success-500, #2e7d32);
    }

    .empty {
      padding: var(--spacing-24, 24px);
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      text-align: center;
      font-size: 13px;
    }

    .load-more {
      display: flex;
      justify-content: center;
      margin-top: var(--spacing-16, 16px);
    }

    bee-tab-group {
      --spacing: 0;
    }

    .action-badge {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 8px;
      border-radius: var(--radius-sm, 6px);
      font-size: 12px;
      font-weight: 500;
      margin-top: var(--spacing-8, 8px);
      font-family: monospace;
    }

    .action-badge.action-create {
      background: var(--colors-background-bg-success-50, #f0fdf4);
      color: var(--colors-foreground-fg-success-700, #15803d);
    }

    .action-badge.action-update {
      background: var(--colors-background-bg-warning-50, #fffcf0);
      color: var(--colors-foreground-fg-warning-700, #b54708);
    }

    .action-badge.action-delete {
      background: var(--colors-background-bg-danger-50, #fef2f2);
      color: var(--colors-foreground-fg-danger-700, #b91c1c);
    }
  `;

  @property({ attribute: false }) activeProductFilter: string | null = null;

  @state() private _entries: AuditEntry[] = [];
  @state() private _loading = true;
  @state() private _loadingMore = false;
  @state() private _error = "";
  @state() private _page = 1;
  @state() private _hasMore = false;
  @state() private _mfeFilter = "";
  @state() private _mfeFilterDraft = "";
  @state() private _exporting = false;

  @state() private _activeTab: "deployments" | "flags" = "deployments";

  @state() private _flagEntries: FlagAuditEntry[] = [];
  @state() private _flagLoading = true;
  @state() private _flagLoadingMore = false;
  @state() private _flagError = "";
  @state() private _flagPage = 1;
  @state() private _flagHasMore = false;
  @state() private _flagExporting = false;

  async connectedCallback() {
    super.connectedCallback();
    if (new URLSearchParams(window.location.search).get("tab") === "flags") {
      this._activeTab = "flags";
    }
    await Promise.all([this._loadEntries(), this._loadFlagEntries()]);
  }

  updated(changed: PropertyValues<this>) {
    // getFlagsAudit is scoped server-side by product, unlike the deployments
    // feed (which fetches everything and filters client-side) — refetch on change.
    if (changed.has("activeProductFilter")) {
      this._loadFlagEntries();
    }
  }

  private async _loadEntries() {
    this._loading = true;
    this._error = "";
    this._page = 1;
    try {
      const res = await api.getAuditLog(1, 25, this._mfeFilter || undefined);
      this._entries = res.entries;
      this._hasMore = res.hasMore;
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to load audit log";
    } finally {
      this._loading = false;
    }
  }

  private async _loadMore() {
    this._loadingMore = true;
    try {
      const nextPage = this._page + 1;
      const res = await api.getAuditLog(nextPage, 25, this._mfeFilter || undefined);
      this._entries = [...this._entries, ...res.entries];
      this._page = nextPage;
      this._hasMore = res.hasMore;
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to load more entries";
    } finally {
      this._loadingMore = false;
    }
  }

  private _onMfeFilterInput(e: CustomEvent) {
    this._mfeFilterDraft = (e.target as HTMLInputElement).value;
  }

  private async _onMfeFilterKeyDown(e: KeyboardEvent) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    await this._submitMfeFilter();
  }

  private async _submitMfeFilter() {
    this._mfeFilter = this._mfeFilterDraft.trim();
    await this._loadEntries();
  }

  private async _clearMfeFilter() {
    if (!this._mfeFilter && !this._mfeFilterDraft) return;
    this._mfeFilterDraft = "";
    this._mfeFilter = "";
    await this._loadEntries();
  }

  private _matchesActiveProduct(entry: AuditEntry): boolean {
    if (!this.activeProductFilter) return true;
    return entry.labels?.includes(`product:${this.activeProductFilter}`) ?? false;
  }

  private async _exportCsv() {
    this._exporting = true;
    this._error = "";
    try {
      const allEntries: AuditEntry[] = [];
      const MAX_EXPORT_PAGES = 40; // GitHub search caps ~1000 results (25/page)
      let page = 1;
      let hasMore = true;
      while (hasMore && page <= MAX_EXPORT_PAGES) {
        const res = await api.getAuditLog(page, 25, this._mfeFilter || undefined);
        if (res.entries.length === 0) break;
        allEntries.push(...res.entries);
        hasMore = res.hasMore;
        page += 1;
      }

      // Scope the export to the currently selected product, matching the on-screen list.
      const scoped = allEntries.filter((e) => this._matchesActiveProduct(e));

      const csv = toCsv<AuditEntry>(scoped, [
        { header: "prNumber", value: (e) => String(e.prNumber) },
        { header: "prUrl", value: (e) => e.prUrl },
        { header: "author", value: (e) => e.author },
        { header: "closedAt", value: (e) => e.closedAt },
        { header: "source", value: (e) => e.source },
        { header: "target", value: (e) => e.target },
        { header: "isHotfix", value: (e) => String(e.isHotfix) },
        { header: "labels", value: (e) => (e.labels ?? []).join(";") },
        { header: "changes", value: (e) => e.changes.map((c) => `${c.mfe}:${c.from}->${c.to}`).join(";") },
      ]);

      const dateStamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const scopeLabel = (this.activeProductFilter || this._mfeFilter || "all").replace(/[^A-Za-z0-9._-]/g, "-");
      downloadCsv(`audit-log-${scopeLabel}-${dateStamp}.csv`, csv);
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to export audit log";
    } finally {
      this._exporting = false;
    }
  }

  private async _loadFlagEntries() {
    this._flagLoading = true;
    this._flagError = "";
    this._flagPage = 1;
    try {
      const res = await api.getFlagsAudit(this.activeProductFilter ?? "", 1, 25);
      this._flagEntries = res.entries;
      this._flagHasMore = res.hasMore;
    } catch (e: unknown) {
      this._flagError = e instanceof Error ? e.message : "Failed to load feature flag audit log";
    } finally {
      this._flagLoading = false;
    }
  }

  private async _loadMoreFlagEntries() {
    this._flagLoadingMore = true;
    try {
      const nextPage = this._flagPage + 1;
      const res = await api.getFlagsAudit(this.activeProductFilter ?? "", nextPage, 25);
      this._flagEntries = [...this._flagEntries, ...res.entries];
      this._flagPage = nextPage;
      this._flagHasMore = res.hasMore;
    } catch (e: unknown) {
      this._flagError = e instanceof Error ? e.message : "Failed to load more entries";
    } finally {
      this._flagLoadingMore = false;
    }
  }

  private async _exportFlagsCsv() {
    this._flagExporting = true;
    this._flagError = "";
    try {
      const allEntries: FlagAuditEntry[] = [];
      const MAX_EXPORT_PAGES = 40;
      let page = 1;
      let hasMore = true;
      while (hasMore && page <= MAX_EXPORT_PAGES) {
        const res = await api.getFlagsAudit(this.activeProductFilter ?? "", page, 25);
        if (res.entries.length === 0) break;
        allEntries.push(...res.entries);
        hasMore = res.hasMore;
        page += 1;
      }

      const csv = toCsv<FlagAuditEntry>(allEntries, [
        { header: "ts", value: (e) => e.ts },
        { header: "user", value: (e) => e.user },
        { header: "product", value: (e) => e.product },
        { header: "env", value: (e) => e.env },
        { header: "flagKey", value: (e) => e.flagKey },
        { header: "action", value: (e) => e.action },
        { header: "from", value: (e) => formatFlagAuditValue(e.from) },
        { header: "to", value: (e) => formatFlagAuditValue(e.to) },
      ]);

      const dateStamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      const scopeLabel = (this.activeProductFilter || "all").replace(/[^A-Za-z0-9._-]/g, "-");
      downloadCsv(`flags-audit-${scopeLabel}-${dateStamp}.csv`, csv);
    } catch (e: unknown) {
      this._flagError = e instanceof Error ? e.message : "Failed to export feature flag audit log";
    } finally {
      this._flagExporting = false;
    }
  }

  private _formatDate(dateStr: string): string {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  render() {
    return html`
      <bee-tab-group
        @bee-tab-show=${(e: CustomEvent<{ name: string }>) => {
          this._activeTab = e.detail.name === "flags" ? "flags" : "deployments";
        }}
      >
        <bee-tab slot="nav" panel="deployments" .active=${this._activeTab === "deployments"} @click=${() => { this._activeTab = "deployments"; }}>Deployments</bee-tab>
        <bee-tab slot="nav" panel="flags" .active=${this._activeTab === "flags"} @click=${() => { this._activeTab = "flags"; }}>Feature Flags</bee-tab>

        <bee-tab-panel name="deployments" .active=${this._activeTab === "deployments"}>
          ${this._renderDeployments()}
        </bee-tab-panel>
        <bee-tab-panel name="flags" .active=${this._activeTab === "flags"}>
          ${this._renderFlags()}
        </bee-tab-panel>
      </bee-tab-group>
    `;
  }

  private _renderDeployments() {
    if (this._loading) {
      return html`<div class="empty"><bee-spinner size="medium" type="blue"></bee-spinner></div>`;
    }
    if (this._error) {
      return html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>`;
    }

    const filtered = this._entries.filter((entry) => this._matchesActiveProduct(entry));

    return html`
      <div class="controls">
        <bee-button label="Refresh" bee-type="secondary-grey" size="small" icon="refresh" @click=${() => this._loadEntries()}></bee-button>
        <bee-button
          label=${this._exporting ? "Exporting..." : "Export CSV"}
          bee-type="secondary-grey"
          size="small"
          ?disabled=${this._exporting}
          @click=${() => this._exportCsv()}
        ></bee-button>
        <div class="filter-actions">
          <bee-input
            class="filter-input"
            .value=${this._mfeFilterDraft}
            placeholder="Search MFE name and press Enter"
            size="small"
            @bee-input=${this._onMfeFilterInput}
            @keydown=${this._onMfeFilterKeyDown}
          ></bee-input>
          <bee-button
            label="Clear"
            bee-type="secondary-grey"
            size="small"
            ?disabled=${!this._mfeFilter && !this._mfeFilterDraft}
            @click=${() => this._clearMfeFilter()}
          ></bee-button>
        </div>
        <span class="entry-count">${filtered.length} entr${filtered.length !== 1 ? "ies" : "y"}</span>
      </div>
      ${filtered.length === 0
        ? html`<div class="empty">No audit log entries found</div>`
        : html`
          <div class="entry-list">
            ${filtered.map((entry) => this._renderEntry(entry))}
          </div>
          ${this._hasMore ? html`
            <div class="load-more">
              <bee-button
                label=${this._loadingMore ? "Loading..." : "Load more"}
                bee-type="secondary-grey"
                size="small"
                ?disabled=${this._loadingMore}
                @click=${() => this._loadMore()}
              ></bee-button>
            </div>
          ` : nothing}
        `}
    `;
  }

  private _renderFlags() {
    if (this._flagLoading) {
      return html`<div class="empty"><bee-spinner size="medium" type="blue"></bee-spinner></div>`;
    }
    if (this._flagError) {
      return html`<bee-alert variant="error" open><span slot="message">${this._flagError}</span></bee-alert>`;
    }

    return html`
      <div class="controls">
        <bee-button label="Refresh" bee-type="secondary-grey" size="small" icon="refresh" @click=${() => this._loadFlagEntries()}></bee-button>
        <bee-button
          label=${this._flagExporting ? "Exporting..." : "Export CSV"}
          bee-type="secondary-grey"
          size="small"
          ?disabled=${this._flagExporting}
          @click=${() => this._exportFlagsCsv()}
        ></bee-button>
        <span class="entry-count">${this._flagEntries.length} entr${this._flagEntries.length !== 1 ? "ies" : "y"}</span>
      </div>
      ${this._flagEntries.length === 0
        ? html`<div class="empty">No feature flag audit log entries found</div>`
        : html`
          <div class="entry-list">
            ${this._flagEntries.map((entry) => this._renderFlagEntry(entry))}
          </div>
          ${this._flagHasMore ? html`
            <div class="load-more">
              <bee-button
                label=${this._flagLoadingMore ? "Loading..." : "Load more"}
                bee-type="secondary-grey"
                size="small"
                ?disabled=${this._flagLoadingMore}
                @click=${() => this._loadMoreFlagEntries()}
              ></bee-button>
            </div>
          ` : nothing}
        `}
    `;
  }

  private _renderFlagEntry(entry: FlagAuditEntry) {
    return html`
      <bee-card>
        <div class="entry-header">
          <div class="entry-left">
            <bee-tag type="badge" color="gray" small noborder>${entry.user}</bee-tag>
            <bee-tag color="gray" small>${formatProductName(entry.product)}</bee-tag>
            <bee-tag color="gray" small>${entry.env}</bee-tag>
          </div>
          <span class="entry-date">${this._formatDate(entry.ts)}</span>
        </div>
        <div class="action-badge action-${entry.action}">${entry.action} &middot; ${entry.flagKey}</div>
        ${entry.action === "update" ? html`
          <div class="change-list">
            <span class="change-chip">
              <span class="change-name">${entry.flagKey}</span>
              <span class="change-from">${formatFlagAuditValue(entry.from)}</span>
              <span class="change-arrow">&rarr;</span>
              <span class="change-to">${formatFlagAuditValue(entry.to)}</span>
            </span>
          </div>
        ` : nothing}
      </bee-card>
    `;
  }

  private _renderEntry(entry: AuditEntry) {
    const badgeClass = entry.target === "prod" ? "to-prod" : "to-stage";
    const productLabels = getProductLabels(entry.labels);

    return html`
      <bee-card>
        <div class="entry-header">
          <div class="entry-left">
            <span class="entry-number">
              <bee-link href=${entry.prUrl} target="_blank" rel="noopener noreferrer">#${entry.prNumber}</bee-link>
            </span>
            <bee-tag type="badge" color="gray" small noborder>${entry.author}</bee-tag>
            ${entry.isHotfix ? html`<bee-tag color="error" small>hotfix</bee-tag>` : nothing}
            ${productLabels.map((p) => html`<bee-tag color="gray" small>${formatProductName(p)}</bee-tag>`)}
          </div>
          <span class="entry-date">${this._formatDate(entry.closedAt)}</span>
        </div>
        <div class="promotion-badge ${badgeClass}">
          ${entry.source} &rarr; ${entry.target}
        </div>
        ${entry.changes.length > 0 ? html`
          <div class="change-list">
            ${entry.changes.map((c) => html`
              <span class="change-chip">
                <span class="change-name">${c.mfe}</span>
                <span class="change-from">${c.from}</span>
                <span class="change-arrow">&rarr;</span>
                <span class="change-to">${c.to}</span>
              </span>
            `)}
          </div>
        ` : nothing}
      </bee-card>
    `;
  }
}
